import { messstelle, messwertLogGeflaggt, messwertLogGeflaggtStatus, } from './../../utils/apiTypes';
import { datenplausibilitaetState } from './datenplausibilitaetTypes';
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { postJsonWithAuth, putWithAuth } from "../../utils/api";
import type { RootState, AppDispatch } from "../../app/store";
import { logError } from "../../utils/utils";
import { openSnackbar } from "../../features/snackbar/globalSnackbarSlice";
import { GridSortModel } from '@material-ui/data-grid';
import { errorHandler } from '../../utils/errorHandling';
import { endOfMonth, startOfMonth } from 'date-fns';
import { subtractTimezoneOffset } from './../../utils/utils';

export const emptyMesswert: messwertLogGeflaggt = {
  id: -1,
  regel: "",
  messstelle: "",
  start: 0,
  ende: 0,
  status: messwertLogGeflaggtStatus.offen,
  kommentar: ""
};

const initialState: datenplausibilitaetState = {
  filterStatus: ["offen", "verwendet", "verwerfen"],
  filterMonat: subtractTimezoneOffset(startOfMonth(new Date())),
  filterMonatEnde: subtractTimezoneOffset(endOfMonth(new Date())),
  aktuellerWert: emptyMesswert,
  geflaggteMesswerteListe: [],
  listLoading: false,
  sorting: [
    {
      field: 'status',
      sort: 'asc',
    },
  ],
  firstLoad: true,
};

const datenplausibilitaetSlice = createSlice({
  name: "datenplausibilitaet",
  initialState,
  reducers: {
    setFilterMonat: (state,  action: PayloadAction<Date>) => {
      state.filterMonat= action.payload;
    },
    setFilterMonatEnde: (state,  action: PayloadAction<Date>) => {
      state.filterMonatEnde = action.payload;
    },
    addStatus: (state, action: PayloadAction<string>) => {
      state.filterStatus.push(action.payload);
    },
    removeStatus: (state, action: PayloadAction<string>) => {
        const index = state.filterStatus.indexOf(action.payload);
        if (index > -1) {
            state.filterStatus.splice(index, 1);
        }
    },
    setAktuellerWert: (state, action: PayloadAction<messwertLogGeflaggt>) => {
      state.aktuellerWert = action.payload;
    },
    setGeflaggteMesswerteListe: (state, action: PayloadAction<Array<messwertLogGeflaggt>>) => {
      state.geflaggteMesswerteListe = action.payload;
      state.firstLoad = false;
    },
    setListLoading: (state, action: PayloadAction<boolean>) => {
      state.listLoading = action.payload;
    },
    setSorting: (state, action: PayloadAction<GridSortModel>) => {
      state.sorting = action.payload;
    },
    updateMesswerte: (state, action: PayloadAction<messwertLogGeflaggt>) => {
      const updateIndex = state.geflaggteMesswerteListe.findIndex(
        (x: messwertLogGeflaggt) => x.id == action.payload.id
      );
      const firstPart = state.geflaggteMesswerteListe.slice(0, updateIndex);
      let lastPart = Array<messwertLogGeflaggt>();
      if (updateIndex + 1 < state.geflaggteMesswerteListe.length)
        lastPart = state.geflaggteMesswerteListe.slice(updateIndex + 1);
      state.geflaggteMesswerteListe = [...firstPart, action.payload, ...lastPart].sort((a, b) => a.messstelle.localeCompare(b.messstelle));
    },
  },

});

export const selectListLoading = (state: RootState): boolean => state.datenplausibilitaet.listLoading;
export const selectGeflaggteMesswerteListe = (state: RootState): messwertLogGeflaggt[] => state.datenplausibilitaet.geflaggteMesswerteListe;
export const selectAktuellerWert = (state: RootState): messwertLogGeflaggt => state.datenplausibilitaet.aktuellerWert;
export const selectSorting = (state: RootState): GridSortModel => state.datenplausibilitaet.sorting;
export const selectFilterStatus = (state: RootState): string[] => state.datenplausibilitaet.filterStatus;
export const selectFilterMonat = (state: RootState): Date => state.datenplausibilitaet.filterMonat;
export const selectFilterMonatEnde = (state: RootState): Date => state.datenplausibilitaet.filterMonatEnde;
export const selectFirstLoad = (state: RootState): boolean => state.datenplausibilitaet.firstLoad;


export const {
  setFilterMonat,
  setFilterMonatEnde,
  addStatus,
  removeStatus,
  setSorting,
  setGeflaggteMesswerteListe,
  setListLoading,
  setAktuellerWert,
  updateMesswerte,
} = datenplausibilitaetSlice.actions;

// ------------------------------------
// ------------ With Body -------------
// ------------------------------------

export const getMesswertGeflaggt = (messstellen: messstelle[], startDatum: Date, endeDatum: Date, status: string[]) => (
  dispatch: AppDispatch
): void => {
  function statusNr(status: string[]) {
      const statusNumber: number[] = [];
      for (let i = 0; i < status.length; i++) {
        let element = -1;
        if (status[i] == "offen") {
          element = 0;
        } else if (status[i] == "verwendet") {
          element = 1;
        } else if (status[i] == "verwerfen") {
          element = 2;
        }
      statusNumber.push(element)
    }
    return statusNumber
  }

  dispatch(setListLoading(true));
  postJsonWithAuth("POSTMESSWERTEGEFLAGGT", {}, {
    start: startDatum, 
    end: endeDatum,       
    messstellen: messstellen.map(x => x.name),
    status: statusNr(status),
  }).then((messwerteGeflaggtResponse: messwertLogGeflaggt[]) => {
    dispatch(setGeflaggteMesswerteListe(messwerteGeflaggtResponse));
  }).catch((error) => {
    logError(error);
    errorHandler(error, dispatch);
  }).finally(() => dispatch(setListLoading(false)));
};

export const putMesswertGeflaggt = (response: messwertLogGeflaggt) => (
  dispatch: AppDispatch
): void => {
  dispatch(setListLoading(true))
  putWithAuth("MESSWERTEGEFLAGGTID", {}, response, response.id.toString()).then(() => {
    dispatch(updateMesswerte(response));
    dispatch(
      openSnackbar({
        message: `Die Auswahl wurde erfolgreich geändert.`,
        severity: "success",
        duration: 6000,
      })
    );
  })
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setListLoading(false)));
};

export default datenplausibilitaetSlice.reducer;
